import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import LoadsTable from '@/pages/loads/components/LoadsTable';
import { type BoardLoad, filterLoads, formatDay, formatTime, lisbonDate, shiftDay, statusLabel } from './board';
import './board.css';

export default function LoadsPage() {
  const [rows, setRows] = useState<BoardLoad[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updated, setUpdated] = useState('');
  const [query, setQuery] = useState('');
  const [day, setDay] = useState('');
  const [status, setStatus] = useState('all');
  const [compact, setCompact] = useState(false);
  const today = lisbonDate();

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: requestError } = await supabase.from('loads').select(`
        id,order_no,client,destination,grade,packaging_type,loading_local,load_date,lot,phone,pager,updated_at,
        status:status_id(id,key,title,completion_pct),
        driver:driver_id(first_name,last_name),carrier:carrier_id(name),
        tractor:tractor_id(registration),trailer:trailer_id(registration)
      `).order('updated_at', { ascending: false }).limit(500);
      if (requestError) throw requestError;
      // FK many-to-one relations can be emitted as objects or arrays by PostgREST typings.
      const one = <T,>(value: T | T[] | null): T | null => Array.isArray(value) ? value[0] ?? null : value ?? null;
      setRows((data ?? []).map((row) => ({ ...row,
        status: one(row.status), driver: one(row.driver), carrier: one(row.carrier),
        tractor: one(row.tractor), trailer: one(row.trailer),
      })) as BoardLoad[]);
      setUpdated(new Date().toISOString());
    } catch (cause) {
      const message = cause && typeof cause === 'object' && 'message' in cause ? String(cause.message) : 'Verifica a ligação e tenta novamente.';
      setError(`Não foi possível carregar as cargas. ${message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void loadData(); }, [loadData]);

  const scopedRows = useMemo(() => filterLoads(rows, day, query), [rows, day, query]);
  const visible = useMemo(() => filterLoads(rows, day, query, status), [rows, day, query, status]);
  const statuses = useMemo(() => {
    const unique = new Map(rows.flatMap((row) => row.status ? [[row.status.id, row.status] as const] : []));
    return [...unique.values()].sort((a, b) => a.completion_pct - b.completion_pct);
  }, [rows]);
  const countStatus = (id: string) => scopedRows.filter((row) => (row.status?.id ?? 'unset') === id).length;
  const active = scopedRows.filter((row) => row.status && row.status.completion_pct > 0 && row.status.completion_pct < 100).length;
  const inLoading = scopedRows.filter((row) => row.status?.key === 'loading').length;
  const withoutDate = rows.filter((row) => !row.load_date).length;
  const reset = () => { setDay(''); setStatus('all'); setQuery(''); };

  return (
    <div className="loads-board">
      <section className="loads-heading" aria-labelledby="loads-title">
        <div>
          <p className="loads-eyebrow">LOGÍSTICA / OPERAÇÃO</p>
          <h1 id="loads-title">Painel de cargas<span className="loads-heading-dot" /></h1>
          <p className="mt-1 text-sm text-foreground-600">Planeamento, portaria e expedição num só ecrã.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-xs text-foreground-600" aria-live="polite">{loading ? 'A atualizar…' : updated ? `Atualizado às ${formatTime(updated)}` : 'A aguardar dados'}</span>
          <button type="button" onClick={() => void loadData()} disabled={loading} className="loads-button">
            <i className={`ri-refresh-line ${loading ? 'animate-spin' : ''}`} aria-hidden="true" /> Atualizar
          </button>
        </div>
      </section>

      <section className="loads-summary" aria-label="Resumo do período e pesquisa selecionados">
        {[
          { label: 'Cargas no período', value: scopedRows.length, icon: 'ri-truck-line', note: day ? formatDay(day) : 'Todas as datas' },
          { label: 'Em operação', value: active, icon: 'ri-route-line', note: 'Da aceitação à expedição' },
          { label: 'Em carga', value: inLoading, icon: 'ri-box-3-line', note: 'Carregamento em curso' },
          { label: 'Entregues', value: scopedRows.filter((row) => row.status?.key === 'delivered').length, icon: 'ri-checkbox-circle-line', note: 'Processo concluído' },
        ].map((metric, index) => (
          <div key={metric.label} className="loads-metric">
            <span className={`loads-metric-icon ${index === 2 ? 'bg-accent-100 text-accent-800' : 'bg-secondary-50 text-secondary-600'}`}><i className={metric.icon} aria-hidden="true" /></span>
            <div><p className="text-xs font-semibold text-foreground-600">{metric.label}</p><p className="loads-metric-value">{loading && !rows.length ? '—' : metric.value}</p><p className="text-xs text-foreground-600">{metric.note}</p></div>
          </div>
        ))}
      </section>

      <section className="loads-panel" aria-label="Quadro operacional de cargas">
        <div className="loads-toolbar">
          <div className="flex flex-wrap items-center gap-2">
            <span className="loads-toolbar-icon"><i className="ri-calendar-2-line" aria-hidden="true" /></span>
            <button type="button" className="loads-date-arrow" aria-label="Dia anterior" onClick={() => setDay(shiftDay(day || today, -1))}><i className="ri-arrow-left-s-line" aria-hidden="true" /></button>
            <label className="sr-only" htmlFor="load-date">Data de carga</label>
            <input id="load-date" type="date" value={day} onChange={(event) => setDay(event.target.value)} className="loads-date" />
            <button type="button" className="loads-date-arrow" aria-label="Dia seguinte" onClick={() => setDay(shiftDay(day || today, 1))}><i className="ri-arrow-right-s-line" aria-hidden="true" /></button>
            <div className="loads-periods" role="group" aria-label="Período">
              {[[today, 'Hoje'], [shiftDay(today, 1), 'Amanhã'], ['', 'Todos']].map(([value, label]) => (
                <button type="button" key={label} onClick={() => setDay(value)} aria-pressed={day === value}>{label}</button>
              ))}
            </div>
          </div>
          <div className="loads-search">
            <i className="ri-search-line" aria-hidden="true" />
            <label className="sr-only" htmlFor="loads-search">Pesquisar cargas</label>
            <input id="loads-search" type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Carga, cliente, matrícula ou motorista…" />
          </div>
        </div>

        <div className="loads-tabs-row">
          <div className="flex flex-wrap gap-1" role="group" aria-label="Filtrar por estado">
            <button type="button" className="loads-tab" aria-pressed={status === 'all'} onClick={() => setStatus('all')}>Todas <span>{scopedRows.length}</span></button>
            {statuses.map((item) => <button type="button" key={item.id} className="loads-tab" aria-pressed={status === item.id} onClick={() => setStatus(item.id)}>{statusLabel(item)} <span>{countStatus(item.id)}</span></button>)}
            {rows.some((row) => !row.status) && <button type="button" className="loads-tab" aria-pressed={status === 'unset'} onClick={() => setStatus('unset')}>Sem estado <span>{countStatus('unset')}</span></button>}
          </div>
          <button type="button" className="loads-density" aria-pressed={compact} onClick={() => setCompact((value) => !value)}><i className="ri-list-check" aria-hidden="true" /> Compacto</button>
        </div>

        {error ? (
          <div className="loads-message" role="alert"><i className="ri-error-warning-line" aria-hidden="true" /><h2>Não foi possível atualizar o painel</h2><p>{error}</p><button type="button" className="loads-button" onClick={() => void loadData()}>Tentar novamente</button></div>
        ) : loading && !rows.length ? (
          <div className="loads-message" role="status"><i className="ri-loader-4-line animate-spin" aria-hidden="true" /><h2>A carregar o quadro operacional</h2><p>A obter as cargas e os dados de transporte.</p></div>
        ) : !visible.length ? (
          <div className="loads-message"><i className="ri-calendar-check-line" aria-hidden="true" /><h2>{rows.length ? 'Sem cargas para esta seleção' : 'Ainda não há cargas disponíveis'}</h2><p>{day ? `Não existem resultados para ${formatDay(day)} com os filtros atuais.` : 'Altera a pesquisa ou remove os filtros para ver as cargas disponíveis.'}</p><button type="button" className="loads-button" onClick={reset}>Ver todas as cargas</button></div>
        ) : <LoadsTable rows={visible} compact={compact} />}

        <footer className="loads-panel-footer">
          <p aria-live="polite"><strong>{visible.length}</strong> de {rows.length} cargas carregadas{withoutDate > 0 ? ` · ${withoutDate} sem data` : ''}</p>
          <div className="flex flex-wrap items-center gap-4"><span>Horas de Portugal · HH:MM = última atualização</span>{(day || query || status !== 'all') && <button type="button" onClick={reset} className="cursor-pointer font-semibold text-primary-700 hover:underline">Limpar filtros</button>}</div>
        </footer>
      </section>
      <p className="loads-footnote"><i className="ri-information-line" aria-hidden="true" /> As cores identificam o estado da carga. Campos não preenchidos são apresentados com «—».{rows.length === 500 && ' Limite de 500 registos mais recentes atingido.'}</p>
    </div>
  );
}
